/**
 * Templates (ADR 0024): a template is a page; its settings live in the page
 * property `nexus.templateConfig`. Placeholders in the text and the properties
 * are filled when a page is made from it.
 *
 *   {{title}}  {{date}}  {{date:dddd D MMMM YYYY}}  {{time}}  {{time:HH:mm}}
 *   {{weekday}}  {{tomorrow}}  {{yesterday}}  {{tomorrow:FORMAT}}
 *   {{Kandidaat}}  → the answer to the question "Kandidaat" (fields)
 *   {{cursor}}     → where the cursor goes (left in place, removed by the editor)
 */

export const TEMPLATE_CONFIG_PROP = 'nexus.templateConfig';

export type TemplateFieldType = 'text' | 'date' | 'page';

export interface TemplateField {
  /** Shown as the question, and used as the placeholder: {{Kandidaat}}. */
  label: string;
  type: TemplateFieldType;
}

export interface TemplateConfig {
  description: string;
  /** Where new pages go: a page or a database (null = top level). */
  targetId: string | null;
  /** Title of new pages, e.g. "Vergadering {{date}} – {{title}}"; empty = ask for the title. */
  titlePattern: string;
  /** Questions asked when the template is used. */
  fields: TemplateField[];
  /** Used for new daily notes. */
  daily: boolean;
  /** Makes a page by itself on a schedule (null = only when used). */
  schedule: TemplateSchedule | null;
}

/** When a recurring template makes its page. `since`: nothing before this moment. */
export interface TemplateSchedule {
  every: 'day' | 'weekdays' | 'week' | 'month';
  /** 0 = Sunday … 6 = Saturday (every week). */
  weekday: number;
  /** 1–31 (every month; short months use their last day). */
  monthDay: number;
  /** "09:00" */
  time: string;
  /** ISO date-time the schedule was set; earlier moments never count. */
  since: string;
}

export const EMPTY_TEMPLATE_CONFIG: TemplateConfig = {
  description: '',
  targetId: null,
  titlePattern: '',
  fields: [],
  daily: false,
  schedule: null,
};

export function readTemplateConfig(raw: unknown): TemplateConfig {
  const v = (raw && typeof raw === 'object' ? raw : {}) as Partial<
    Record<keyof TemplateConfig, unknown>
  >;
  const fields = Array.isArray(v.fields)
    ? v.fields
        .map((f) => f as Partial<TemplateField>)
        .filter((f) => typeof f.label === 'string' && f.label.trim())
        .map((f) => ({
          label: f.label!.trim(),
          type: (['text', 'date', 'page'] as const).includes(f.type as TemplateFieldType)
            ? (f.type as TemplateFieldType)
            : 'text',
        }))
    : [];
  return {
    description: typeof v.description === 'string' ? v.description : '',
    targetId: typeof v.targetId === 'string' && v.targetId ? v.targetId : null,
    titlePattern: typeof v.titlePattern === 'string' ? v.titlePattern : '',
    fields,
    daily: v.daily === true,
    schedule: readSchedule(v.schedule),
  };
}

function readSchedule(raw: unknown): TemplateSchedule | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Partial<Record<keyof TemplateSchedule, unknown>>;
  if (!['day', 'weekdays', 'week', 'month'].includes(v.every as string)) return null;
  const int = (x: unknown, min: number, max: number, dflt: number) =>
    typeof x === 'number' && Number.isInteger(x) && x >= min && x <= max ? x : dflt;
  return {
    every: v.every as TemplateSchedule['every'],
    weekday: int(v.weekday, 0, 6, 1),
    monthDay: int(v.monthDay, 1, 31, 1),
    time: typeof v.time === 'string' && /^\d{1,2}:\d{2}$/.test(v.time) ? v.time : '09:00',
    since: typeof v.since === 'string' ? v.since : new Date(0).toISOString(),
  };
}

/**
 * The latest scheduled moment at or before `now` (local time), or null when it
 * would be before the schedule was set. Missed moments do not pile up: only the
 * last one counts.
 */
export function latestOccurrence(s: TemplateSchedule, now: Date): Date | null {
  const [h, m] = s.time.split(':').map(Number) as [number, number];
  const at = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m);
  const fits = (d: Date) => {
    if (s.every === 'day') return true;
    if (s.every === 'weekdays') return d.getDay() >= 1 && d.getDay() <= 5;
    if (s.every === 'week') return d.getDay() === s.weekday;
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return d.getDate() === Math.min(s.monthDay, last);
  };
  const since = new Date(s.since);
  for (let i = 0; i < 32; i++) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const moment = at(day);
    if (moment > now || !fits(day)) continue;
    return moment >= since ? moment : null;
  }
  return null;
}

/** Whether using the template needs a title from the user. */
export const asksTitle = (c: TemplateConfig) =>
  !c.titlePattern || /\{\{\s*title\s*\}\}/i.test(c.titlePattern);

// ---- dates ---------------------------------------------------------------------------

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/**
 * Moment-style formats as in Obsidian: YYYY YY MMMM MMM MM M DD D dddd ddd HH H mm ss,
 * text in [brackets] is kept as is.
 */
export function formatDate(d: Date, format: string, locale: string): string {
  const name = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, opts).format(d);
  const tokens: Record<string, () => string> = {
    YYYY: () => String(d.getFullYear()),
    YY: () => pad(d.getFullYear() % 100),
    MMMM: () => name({ month: 'long' }),
    MMM: () => name({ month: 'short' }).replace(/\.$/, ''),
    MM: () => pad(d.getMonth() + 1),
    M: () => String(d.getMonth() + 1),
    DD: () => pad(d.getDate()),
    D: () => String(d.getDate()),
    dddd: () => name({ weekday: 'long' }),
    ddd: () => name({ weekday: 'short' }).replace(/\.$/, ''),
    HH: () => pad(d.getHours()),
    H: () => String(d.getHours()),
    mm: () => pad(d.getMinutes()),
    ss: () => pad(d.getSeconds()),
  };
  return format.replace(
    /\[([^\]]*)\]|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|mm|ss/g,
    (m, lit?: string) => (lit !== undefined ? lit : tokens[m]!()),
  );
}

// ---- placeholders ----------------------------------------------------------------------

export interface TemplateVars {
  title: string;
  now: Date;
  locale: string;
  /** Answers by question label. */
  fields?: Record<string, string>;
}

const addDaysTo = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/** Fill the placeholders in a text; unknown ones (and {{cursor}}) stay. */
export function fillTemplateText(text: string, vars: TemplateVars): string {
  if (!text.includes('{{')) return text;
  const fields = new Map(
    Object.entries(vars.fields ?? {}).map(([k, v]) => [k.trim().toLowerCase(), v]),
  );
  return text.replace(
    /\{\{\s*([^{}:]+?)\s*(?::([^{}]*))?\}\}/g,
    (m, rawName: string, format?: string) => {
      const key = rawName.toLowerCase();
      const day = (offset: number) =>
        formatDate(addDaysTo(vars.now, offset), format?.trim() || 'YYYY-MM-DD', vars.locale);
      switch (key) {
        case 'title':
          return vars.title;
        case 'date':
          return day(0);
        case 'tomorrow':
          return day(1);
        case 'yesterday':
          return day(-1);
        case 'time':
          return formatDate(vars.now, format?.trim() || 'HH:mm', vars.locale);
        case 'weekday':
          return formatDate(vars.now, 'dddd', vars.locale);
        case 'cursor':
          return m;
      }
      return fields.has(key) ? fields.get(key)! : m;
    },
  );
}

/** Fill placeholders in property values (strings, and strings in lists). */
export function fillTemplateValue(value: unknown, vars: TemplateVars): unknown {
  if (typeof value === 'string') return fillTemplateText(value, vars);
  if (Array.isArray(value)) return value.map((v) => fillTemplateValue(v, vars));
  return value;
}
